import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { strapiDelete, strapiGet, strapiPost, strapiPut } from '@/lib/strapi'
import { useAppStore } from '@/lib/store/useAppStore'

export type CouponDiscountType = 'percentage' | 'fixed_amount' | 'free_delivery'
export type CouponFulfillmentRule = 'all' | 'pickup' | 'delivery'
export type CouponMenuRule = 'all' | 'categories' | 'items'

export interface Coupon {
  id?: number
  documentId: string
  bookosId: string
  code: string
  name?: string | null
  active: boolean
  discountType: CouponDiscountType
  discountValue: number | string
  minimumSubtotal?: number | string | null
  maximumDiscount?: number | string | null
  startsAt?: string | null
  endsAt?: string | null
  usageLimit?: number | null
  appliesToFulfillment?: CouponFulfillmentRule
  appliesToMenu?: CouponMenuRule
  menuCategories?: Array<{ documentId: string; name: string }>
  menuItems?: Array<{ documentId: string; name: string; category?: { documentId: string; name: string } | null }>
  createdAt?: string
  updatedAt?: string
}

export interface CouponInput {
  code: string
  name?: string
  active?: boolean
  discountType: CouponDiscountType
  discountValue?: number
  minimumSubtotal?: number
  maximumDiscount?: number | null
  startsAt?: string | null
  endsAt?: string | null
  usageLimit?: number | null
  appliesToFulfillment?: CouponFulfillmentRule
  appliesToMenu?: CouponMenuRule
  menuCategoryDocumentIds?: string[]
  menuItemDocumentIds?: string[]
}

export function useCoupons() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useQuery({
    queryKey: ['coupons', restaurantId],
    queryFn: () => strapiGet<Coupon[]>('/api/pos/coupons'),
    enabled: !!restaurantId,
    staleTime: 30_000,
  })
}

export function useCreateCoupon() {
  const qc = useQueryClient()
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: (data: CouponInput) =>
      strapiPost<Coupon>('/api/pos/coupons', {
        data: {
          ...data,
          bookosId: `coup-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coupons', restaurantId] })
      toast.success('Coupon created')
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to create coupon'),
  })
}

export function useUpdateCoupon() {
  const qc = useQueryClient()
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: ({ documentId, ...data }: { documentId: string } & Partial<CouponInput>) =>
      strapiPut<Coupon>(`/api/pos/coupons/${documentId}`, { data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coupons', restaurantId] })
      toast.success('Coupon updated')
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to update coupon'),
  })
}

export function useDeleteCoupon() {
  const qc = useQueryClient()
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useMutation({
    mutationFn: (documentId: string) => strapiDelete(`/api/pos/coupons/${documentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coupons', restaurantId] })
      toast.success('Coupon deleted')
    },
    onError: () => toast.error('Failed to delete coupon'),
  })
}
